/**
 * MultiLaneTimeline — Container for all lanes with zoom/pan/scrubber
 *
 * Handles:
 *  - Horizontal scrolling via wheel
 *  - Zoom via Ctrl+wheel
 *  - TimeRuler at top
 *  - All lane components stacked vertically
 *  - Scrubber playhead overlay
 */
import { useRef, useCallback, useEffect, useState, useMemo } from 'react';
import type {
  VehicleSceneTimelineResponse,
  TimelineSelection,
  TimelineHover,
  ViewMode,
  LaneVisibility,
} from '../../types/vehicleSceneTimeline';
import {
  toMs,
  computeScale,
  clampOffset,
  MIN_ZOOM,
  MAX_ZOOM,
  type ZoomPanState,
  type TimeScale,
} from '../../utils/timelineUtils';

import TimeRuler from './TimeRuler';
import Scrubber from './Scrubber';
import MovementLane from './lanes/MovementLane';
import OrdersLane from './lanes/OrdersLane';
import DriversLane from './lanes/DriversLane';
import FuelLane from './lanes/FuelLane';
import AttributionSlicesLane from './lanes/AttributionSlicesLane';

interface MultiLaneTimelineProps {
  data: VehicleSceneTimelineResponse;
  viewMode: ViewMode;
  lanes: LaneVisibility;
  zoomPan: ZoomPanState;
  onZoomPan: React.Dispatch<React.SetStateAction<ZoomPanState>>;
  selection: TimelineSelection | null;
  selectSegment: (id: string) => void;
  selectOrder: (id: string) => void;
  selectDriver: (id: string) => void;
  selectFuel: (id: string) => void;
  selectSlice: (id: string) => void;
  hover: TimelineHover | null;
  onHover: (h: TimelineHover | null) => void;
  scrubberMs: number | null;
  onScrub: (ms: number | null) => void;
}

const LABEL_WIDTH = 120;
const RULER_HEIGHT = 32;

export default function MultiLaneTimeline({
  data,
  viewMode,
  lanes,
  zoomPan,
  onZoomPan,
  selection,
  selectSegment,
  selectOrder,
  selectDriver,
  selectFuel,
  selectSlice,
  hover,
  onHover,
  scrubberMs,
  onScrub,
}: MultiLaneTimelineProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(800);

  // Measure container
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width - LABEL_WIDTH);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Time window bounds
  const windowFromMs = useMemo(() => toMs(data.window.from), [data.window.from]);
  const windowToMs = useMemo(() => toMs(data.window.to), [data.window.to]);

  // Compute drawing scale
  const scale: TimeScale = useMemo(
    () => computeScale(windowFromMs, windowToMs, containerWidth, zoomPan),
    [windowFromMs, windowToMs, containerWidth, zoomPan],
  );

  // Wheel: Ctrl+wheel = zoom, plain wheel = pan
  const handleWheel = useCallback(
    (e: WheelEvent) => {
      e.preventDefault();
      const totalDuration = windowToMs - windowFromMs;

      if (e.ctrlKey || e.metaKey) {
        const zoomDelta = e.deltaY < 0 ? 1.15 : 1 / 1.15;
        onZoomPan(prev => {
          const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, prev.zoom * zoomDelta));
          const rect = containerRef.current?.getBoundingClientRect();
          const cursorX = rect ? e.clientX - rect.left - LABEL_WIDTH : containerWidth / 2;
          const cursorRatio = cursorX / containerWidth;
          const visibleBefore = totalDuration / prev.zoom;
          const visibleAfter = totalDuration / newZoom;
          const newOffset = prev.offsetMs + (visibleBefore - visibleAfter) * cursorRatio;
          return {
            zoom: newZoom,
            offsetMs: clampOffset(newOffset, windowFromMs, windowToMs, newZoom),
          };
        });
      } else {
        const panMs = (e.deltaX + e.deltaY) * (totalDuration / zoomPan.zoom) * 0.001;
        onZoomPan(prev => ({
          ...prev,
          offsetMs: clampOffset(prev.offsetMs + panMs, windowFromMs, windowToMs, prev.zoom),
        }));
      }
    },
    [windowFromMs, windowToMs, containerWidth, zoomPan.zoom, onZoomPan],
  );

  // Attach wheel listener as non-passive so preventDefault works
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  // Track total lane height for scrubber
  const lanesContainerRef = useRef<HTMLDivElement>(null);
  const [totalLaneHeight, setTotalLaneHeight] = useState(300);
  useEffect(() => {
    const el = lanesContainerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setTotalLaneHeight(entry.contentRect.height);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      className="vst-timeline-container"
    >
      {/* Zoom indicator */}
      <div className="vst-zoom-badge">
        {zoomPan.zoom.toFixed(1)}x
      </div>

      {/* Time Ruler */}
      <div className="vst-ruler-row">
        <div className="vst-lane-label-spacer" />
        <TimeRuler scale={scale} height={RULER_HEIGHT} />
      </div>

      {/* Lanes */}
      <div className="vst-lanes-area" ref={lanesContainerRef} style={{ position: 'relative' }}>
        {lanes.movement && (
          <MovementLane
            segments={data.segments}
            scale={scale}
            selection={selection}
            hover={hover}
            onSelect={selectSegment}
            onHover={onHover}
          />
        )}

        {lanes.orders && (
          <OrdersLane
            orders={data.overlays.orders}
            plannedOrders={data.overlays.planned_orders}
            deliveries={data.overlays.deliveries}
            scale={scale}
            viewMode={viewMode}
            selection={selection}
            hover={hover}
            onSelect={selectOrder}
            onHover={onHover}
          />
        )}

        {lanes.drivers && (
          <DriversLane
            drivers={data.overlays.drivers}
            scale={scale}
            selection={selection}
            hover={hover}
            onSelect={selectDriver}
            onHover={onHover}
          />
        )}

        {lanes.fuel && (
          <FuelLane
            fuel={data.overlays.fuel}
            scale={scale}
            selection={selection}
            hover={hover}
            onSelect={selectFuel}
            onHover={onHover}
          />
        )}

        {lanes.attribution && (
          <AttributionSlicesLane
            slices={data.attribution_slices}
            scale={scale}
            selection={selection}
            hover={hover}
            onSelect={selectSlice}
            onHover={onHover}
          />
        )}

        {/* Scrubber overlay */}
        <Scrubber
          scale={scale}
          scrubberMs={scrubberMs}
          onScrub={onScrub}
          data={data}
          totalHeight={totalLaneHeight}
        />
      </div>
    </div>
  );
}
